import { createTheme, type CSSVariablesResolver, type MantineColorsTuple } from '@mantine/core';

/**
 * Barvy drží paletu mobilní aplikace (`mobile/src/theme/index.ts`), aby admin web
 * a zařízení v terénu vypadaly jako jeden produkt:
 *
 *   light  pozadí #f5f5f5 · povrch #ffffff · primární #2e7d32
 *   dark   pozadí #121212 · povrch #1e1e1e · primární #4caf50
 *
 * Neutrály jsou čistě šedé, bez barevného nádechu — appka to tak má taky.
 */

/** Material Green — index 8 je #2E7D32 (light), index 5 #4CAF50 (dark). */
const forest: MantineColorsTuple = [
  '#E8F5E9', '#C8E6C9', '#A5D6A7', '#81C784', '#66BB6A',
  '#4CAF50', '#43A047', '#388E3C', '#2E7D32', '#1B5E20',
];

/**
 * Jediná barva mimo paletu appky. Vyhrazená výhradně pro stav „běží teď“, aby se
 * běžící hra nedala splést s varováním (amber) ani s chybou (červená). Vzata
 * z Material Pink, tedy ze stejné rodiny jako zbytek palety.
 */
const flag: MantineColorsTuple = [
  '#FCE4EC', '#F8BBD0', '#F48FB1', '#F06292', '#EC407A',
  '#E91E63', '#D81B60', '#C2185B', '#AD1457', '#880E4F',
];

/** Neutrální šedá pro světlý režim (Mantine default táhne do modra). */
const gray: MantineColorsTuple = [
  '#F5F5F5', '#F0F0F0', '#E0E0E0', '#D6D6D6', '#BDBDBD',
  '#9E9E9E', '#767676', '#666666', '#4A4A4A', '#333333',
];

/**
 * Tmavá škála podle appky. Pozor na index 3: appka má textTertiary #666666, což je
 * na drobných popiscích pod 3:1, takže ho tu držíme světlejší.
 */
const dark: MantineColorsTuple = [
  '#E8E8E8', '#C9C9C9', '#A0A0A0', '#8A8A8A', '#5C5C5C',
  '#333333', '#1E1E1E', '#121212', '#0D0D0D', '#000000',
];

const mono =
  'ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

export const theme = createTheme({
  primaryColor: 'forest',
  primaryShade: { light: 8, dark: 5 },
  colors: { forest, flag, gray, dark },

  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace: mono,
  headings: { fontWeight: '700' },

  defaultRadius: 'sm',
  radius: { xs: '3px', sm: '5px', md: '8px', lg: '12px', xl: '16px' },

  fontSizes: {
    xs: '11px', sm: '12.5px', md: '14px', lg: '16px', xl: '19px',
  },

  components: {
    Card: { defaultProps: { withBorder: true, radius: 'md', padding: 'md' } },
    Paper: { defaultProps: { withBorder: true, radius: 'md' } },
    Table: { defaultProps: { verticalSpacing: 7, horizontalSpacing: 12, fz: 'sm' } },
    Modal: { defaultProps: { radius: 'md', centered: true } },
    Button: { defaultProps: { size: 'xs' } },
    ActionIcon: { defaultProps: { variant: 'subtle', color: 'gray' } },
    Badge: { defaultProps: { radius: 'xl', variant: 'light' } },
    TextInput: { defaultProps: { size: 'sm' } },
    NumberInput: { defaultProps: { size: 'sm' } },
    Select: { defaultProps: { size: 'sm' } },
  },
});

/** Popisková řeč mapy — mono verzálky pro legendy, kódy a časy. */
export const eyebrowStyle = {
  fontFamily: mono,
  fontSize: '10px',
  letterSpacing: '0.1em',
  textTransform: 'uppercase' as const,
  fontWeight: 500,
};

/** Číslice, které mají lícovat ve sloupcích. */
export const numStyle = {
  fontFamily: mono,
  fontVariantNumeric: 'tabular-nums' as const,
};

/**
 * Mantine si část proměnných odvozuje po svém a mine tím paletu appky:
 *  - podklad ve světlém režimu je bílý, appka má #f5f5f5 (a karty by na bílé zanikly)
 *  - rámeček bere z dark-4 / gray-4, appka má #333333 / #e0e0e0
 * Tohle to srovná zpátky na mobile/src/theme/index.ts.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-body': '#F5F5F5',
    '--mantine-color-default-border': '#E0E0E0',
    // Mantine tu jinak sáhne po theme.black (#000); appka má #333333.
    '--mantine-color-text': '#333333',
  },
  dark: {
    '--mantine-color-body': '#121212',
    '--mantine-color-default-border': '#333333',
  },
});
